import React, { useEffect, useState, useCallback } from 'react';
import {
  Drawer,
  Box,
  Stack,
  Typography,
  IconButton,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Skeleton,
  Button,
  useTheme,
  useMediaQuery,
  alpha,
  Divider,
  styled,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import MemoryIcon from '@mui/icons-material/Memory';
import ChatIcon from '@mui/icons-material/Chat';
import ForumIcon from '@mui/icons-material/Forum';
import LoopIcon from '@mui/icons-material/Loop';
import TimelineIcon from '@mui/icons-material/Timeline';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import BuildIcon from '@mui/icons-material/Build';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import API from '@Services/apiAxios';
import dayjs from 'dayjs';
import Title from '@Components/Title';

// Interfaces for response structure
export interface OverallAnalytics {
  totalCost: number;
  totalTokens: number;
  totalRounds: number;
  totalSessions: number;
  avgCostPerRound: number;
  avgTokensPerRound: number;
  avgRoundsPerSession: number;
}

export interface SessionAnalytics {
  orgCode: string;
  totalCost: number;
  totalTokens: number;
  roundCount: number;
  sessionId: string;
}

export interface ToolAnalytics {
  totalCost: number;
  totalTokens: number;
  callCount: number;
  tool: string;
}

export interface UserToolUsage {
  tool: string;
  callCount: number;
}

export interface UserAnalytics {
  totalCost: number;
  totalTokens: number;
  roundCount: number;
  userId: string;
  toolUsage: UserToolUsage[];
}

export interface UserDetail {
  _id: string;
  name: string;
  email: string;
  onBoardedStatus: string;
}

export interface UserDetailsResponse {
  msg: string;
  data: UserDetail[];
}

export interface LLMUsageResponse {
  overall: OverallAnalytics;
  sessionWise: SessionAnalytics[];
  toolWise: ToolAnalytics[];
  userWise?: UserAnalytics[];
}

interface BotAnalyticsDrawerProps {
  open: boolean;
  onClose: () => void;
}

// Utility to format costs dynamically based on precision
const formatCost = (value: number | undefined): string => {
  if (value === undefined || value === null) return '$0.00';
  if (value === 0) return '$0.00';
  if (value < 0.01) {
    return `${Number(value.toFixed(6))}`;
  }
  return `${value.toFixed(4)}`;
};

// Clipboard copy component with Tooltip
const CopyableSessionId: React.FC<{ id: string }> = ({ id }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(id);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const truncated = id ? `${id.substring(0, 6)}...${id.substring(id.length - 4)}` : '';

  return (
    <Stack direction="row" alignItems="center" gap={0.5} sx={{ display: 'inline-flex' }}>
      <Tooltip title={id} placement="top" arrow>
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'monospace',
            fontSize: '13px',
            color: 'text.primary',
            fontWeight: 500,
          }}
        >
          {truncated}
        </Typography>
      </Tooltip>
      <Tooltip title={copied ? 'Copied!' : 'Copy Session ID'}>
        <IconButton size="small" onClick={handleCopy} sx={{ p: 0.25 }}>
          {copied ? (
            <CheckIcon fontSize="small" color="success" sx={{ fontSize: '14px' }} />
          ) : (
            <ContentCopyIcon fontSize="small" sx={{ fontSize: '13px', color: 'text.secondary' }} />
          )}
        </IconButton>
      </Tooltip>
    </Stack>
  );
};

// Skeletons for summary cards
const SummarySkeletons = () => (
  <Grid container spacing={2}>
    {Array.from({ length: 6 }).map((_, idx) => (
      <Grid item xs={6} key={idx}>
        <Card variant="outlined" sx={{ borderRadius: '12px', border: '1px solid #eaecf0' }}>
          <CardContent sx={{ p: 1.5 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Skeleton variant="circular" width={32} height={32} />
              <Stack spacing={0.5} flex={1}>
                <Skeleton variant="text" width="60%" height={16} />
                <Skeleton variant="text" width="80%" height={24} />
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Grid>
    ))}
  </Grid>
);

// Skeletons for tool usage cards
const ToolSkeletons = () => (
  <Stack spacing={1.5}>
    {Array.from({ length: 2 }).map((_, idx) => (
      <Card key={idx} variant="outlined" sx={{ borderRadius: '12px', border: '1px solid #eaecf0', p: 2 }}>
        <Stack spacing={1.5}>
          <Stack direction="row" justifyContent="space-between">
            <Skeleton variant="text" width="40%" height={20} />
            <Skeleton variant="rectangular" width={60} height={20} sx={{ borderRadius: '4px' }} />
          </Stack>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Skeleton variant="text" width="30%" height={14} />
              <Skeleton variant="text" width="60%" height={18} />
            </Grid>
            <Grid item xs={6}>
              <Skeleton variant="text" width="30%" height={14} />
              <Skeleton variant="text" width="60%" height={18} />
            </Grid>
          </Grid>
        </Stack>
      </Card>
    ))}
  </Stack>
);

// Skeletons for user usage cards
const UserSkeletons = () => (
  <Stack spacing={1.5}>
    {Array.from({ length: 2 }).map((_, idx) => (
      <Card key={idx} variant="outlined" sx={{ borderRadius: '12px', border: '1px solid #eaecf0', p: 2 }}>
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Skeleton variant="circular" width={38} height={38} />
            <Stack spacing={0.5} flex={1}>
              <Skeleton variant="text" width="40%" height={18} />
              <Skeleton variant="text" width="60%" height={14} />
            </Stack>
          </Stack>
          <Skeleton variant="text" width="80%" height={14} />
          <Grid container spacing={2}>
            {Array.from({ length: 3 }).map((__, sIdx) => (
              <Grid item xs={4} key={sIdx}>
                <Skeleton variant="text" width="30%" height={12} />
                <Skeleton variant="text" width="70%" height={16} />
              </Grid>
            ))}
          </Grid>
          <Stack direction="row" spacing={1}>
            <Skeleton variant="rectangular" width={100} height={20} sx={{ borderRadius: '6px' }} />
            <Skeleton variant="rectangular" width={80} height={20} sx={{ borderRadius: '6px' }} />
          </Stack>
        </Stack>
      </Card>
    ))}
  </Stack>
);

export const BotAnalyticsDrawer: React.FC<BotAnalyticsDrawerProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [data, setData] = useState<LLMUsageResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [userDetails, setUserDetails] = useState<Record<string, UserDetail>>({});
  const [userDetailsLoading, setUserDetailsLoading] = useState<boolean>(false);

  // Active dates calculated dynamically
  const toDateStr = dayjs().format('YYYY-MM-DD');
  const fromDateStr = dayjs().subtract(1, 'year').format('YYYY-MM-DD');

  const fetchUserDetails = useCallback(async (userWise: UserAnalytics[]) => {
    if (!userWise || userWise.length === 0) {
      setUserDetails({});
      setUserDetailsLoading(false);
      return;
    }

    setUserDetailsLoading(true);
    try {
      // Extract unique userIds
      const uniqueUserIds = Array.from(new Set(userWise.map(u => u.userId).filter(Boolean)));
      if (uniqueUserIds.length === 0) {
        setUserDetails({});
        setUserDetailsLoading(false);
        return;
      }

      const res = await API.post<UserDetailsResponse>('/user/getUserDetails', {
        userIds: uniqueUserIds,
      });

      if (res.data && Array.isArray(res.data.data)) {
        const detailsMap: Record<string, UserDetail> = {};
        res.data.data.forEach(user => {
          if (user && user._id) {
            detailsMap[user._id] = user;
          }
        });
        setUserDetails(detailsMap);
      }
    } catch (err) {
      console.error('Error fetching user details:', err);
    } finally {
      setUserDetailsLoading(false);
    }
  }, []);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);
    setUserDetails({});
    setUserDetailsLoading(false);
    try {
      const response = await API.get<LLMUsageResponse>('/speedboxbot/analytics/llm-usage', {
        params: {
          toDate: toDateStr,
          fromDate: fromDateStr,
        },
      });
      const responseData = response.data;
      setData(responseData);
      setLoading(false);

      if (responseData.userWise && responseData.userWise.length > 0) {
        fetchUserDetails(responseData.userWise);
      }
    } catch (err: unknown) {
      console.error('Error fetching bot analytics:', err);
      const errorObj = err as { msg?: string; message?: string };
      setError(errorObj?.msg || errorObj?.message || 'Failed to load bot analytics data.');
      setLoading(false);
    }
  }, [toDateStr, fromDateStr, fetchUserDetails]);

  useEffect(() => {
    if (open) {
      fetchAnalytics();
    }
  }, [open, fetchAnalytics]);

  // Formatted date range label for UI
  const dateRangeLabel = `${dayjs(fromDateStr).format('MMM DD, YYYY')} - ${dayjs(toDateStr).format('MMM DD, YYYY')}`;

  const isDataEmpty =
    !data ||
    (!data.overall.totalSessions &&
      !data.overall.totalCost &&
      (!data.sessionWise || data.sessionWise.length === 0) &&
      (!data.toolWise || data.toolWise.length === 0) &&
      (!data.userWise || data.userWise.length === 0));

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      variant="temporary"
      ModalProps={{
        keepMounted: true,
      }}
      sx={{
        '& .MuiBackdrop-root': {
          backdropFilter: 'blur(4px)',
          backgroundColor: 'rgba(0,0,0,0.35)',
        },
        '& .MuiDrawer-paper': {
          width: isSmallScreen ? '100%' : '42rem',
          backgroundImage: 'none',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          backgroundColor: alpha(theme.palette.background.paper, 0.98),
          boxShadow: '-8px 0px 32px rgba(0, 0, 0, 0.1)',
        },
      }}
    >
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Drawer Header */}
        <Box sx={{ p: '2rem 1.5rem 1rem 2rem', borderBottom: '1px solid #eaecf0' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" alignItems="center" gap={1.5}>
              <SmartToyIcon sx={{ color: 'primary.main', fontSize: 28 }} />
              <Title title="Bot Analytics" removeBorder removePadding />
            </Stack>
            <Stack direction="row" spacing={1}>
              <Tooltip title="Refresh Data">
                <IconButton onClick={fetchAnalytics} disabled={loading} size="medium">
                  <RefreshIcon
                    sx={{
                      animation: loading ? 'spin 1.5s linear infinite' : 'none',
                      '@keyframes spin': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' },
                      },
                    }}
                  />
                </IconButton>
              </Tooltip>
              <IconButton onClick={onClose} size="medium">
                <CloseIcon />
              </IconButton>
            </Stack>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontWeight: 500 }}>
            Usage Statistics: <span style={{ color: theme.palette.text.primary }}>{dateRangeLabel}</span>
          </Typography>
        </Box>

        {/* Drawer Content */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: '1.5rem 2rem 2rem 2rem',
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}
        >
          {loading && !data ? (
            // Full screen skeleton on initial fetch
            <Stack spacing={3}>
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}
                >
                  Overview
                </Typography>
                <SummarySkeletons />
              </Box>
              <Divider />
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}
                >
                  Session Usage
                </Typography>
                <Skeleton variant="rectangular" height={150} sx={{ borderRadius: '12px' }} />
              </Box>
              <Divider />
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}
                >
                  Tool Usage
                </Typography>
                <ToolSkeletons />
              </Box>
              <Divider />
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}
                >
                  User Usage Analytics
                </Typography>
                <UserSkeletons />
              </Box>
            </Stack>
          ) : error ? (
            // Error handling
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                flex: 1,
                py: 4,
                textAlign: 'center',
              }}
            >
              <ErrorOutlineIcon sx={{ color: 'error.main', fontSize: 48, mb: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Failed to Load Analytics
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: '280px' }}>
                {error}
              </Typography>
              <Button variant="contained" color="primary" onClick={fetchAnalytics} startIcon={<RefreshIcon />}>
                Try Again
              </Button>
            </Box>
          ) : isDataEmpty ? (
            // Empty state handling
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                flex: 1,
                py: 4,
                textAlign: 'center',
              }}
            >
              <SmartToyIcon sx={{ color: 'text.secondary', fontSize: 54, mb: 2, opacity: 0.5 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                No Usage Found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '280px' }}>
                We couldn't find any LLM bot usage data for the past 1 year.
              </Typography>
            </Box>
          ) : (
            // Normal display
            <>
              {/* Overall section */}
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    mb: 2,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    color: 'text.secondary',
                    fontSize: '11px',
                  }}
                >
                  Overview
                </Typography>

                <Grid container spacing={2}>
                  {/* Cards config array */}
                  {[
                    {
                      title: 'Total Cost',
                      value: formatCost(data.overall.totalCost),
                      icon: <AttachMoneyIcon sx={{ color: 'primary.main', fontSize: '20px' }} />,
                      bgColor: alpha(theme.palette.primary.main, 0.04),
                      borderColor: alpha(theme.palette.primary.main, 0.1),
                    },
                    {
                      title: 'Total Tokens',
                      value: data.overall.totalTokens.toLocaleString(),
                      icon: <MemoryIcon sx={{ color: 'secondary.main', fontSize: '20px' }} />,
                      bgColor: alpha(theme.palette.secondary.main, 0.04),
                      borderColor: alpha(theme.palette.secondary.main, 0.1),
                    },
                    {
                      title: 'Total Sessions',
                      value: data.overall.totalSessions.toLocaleString(),
                      icon: <ForumIcon sx={{ color: 'info.main', fontSize: '20px' }} />,
                      bgColor: alpha(theme.palette.info.main, 0.04),
                      borderColor: alpha(theme.palette.info.main, 0.1),
                    },
                    {
                      title: 'Total Rounds',
                      value: data.overall.totalRounds.toLocaleString(),
                      icon: <ChatIcon sx={{ color: 'success.main', fontSize: '20px' }} />,
                      bgColor: alpha(theme.palette.success.main, 0.04),
                      borderColor: alpha(theme.palette.success.main, 0.1),
                    },
                    {
                      title: 'Avg Cost / Round',
                      value: formatCost(data.overall.avgCostPerRound),
                      icon: <MonetizationOnIcon sx={{ color: 'primary.main', fontSize: '20px' }} />,
                      bgColor: alpha(theme.palette.primary.main, 0.04),
                      borderColor: alpha(theme.palette.primary.main, 0.1),
                    },
                    {
                      title: 'Avg Tokens / Round',
                      value:
                        data.overall.avgTokensPerRound !== undefined ? data.overall.avgTokensPerRound.toFixed(1) : '0',
                      icon: <TimelineIcon sx={{ color: 'secondary.main', fontSize: '20px' }} />,
                      bgColor: alpha(theme.palette.secondary.main, 0.04),
                      borderColor: alpha(theme.palette.secondary.main, 0.1),
                    },
                    {
                      title: 'Avg Rounds / Session',
                      value:
                        data.overall.avgRoundsPerSession !== undefined
                          ? data.overall.avgRoundsPerSession.toFixed(1)
                          : '0',
                      icon: <LoopIcon sx={{ color: 'success.main', fontSize: '20px' }} />,
                      bgColor: alpha(theme.palette.success.main, 0.04),
                      borderColor: alpha(theme.palette.success.main, 0.1),
                      gridSize: 12,
                    },
                  ].map((card, idx) => (
                    <Grid item xs={card.gridSize || 6} key={idx}>
                      <Card
                        variant="outlined"
                        sx={{
                          borderRadius: '12px',
                          borderColor: card.borderColor,
                          backgroundColor: card.bgColor,
                          transition: 'transform 0.2s, box-shadow 0.2s',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.04)',
                          },
                        }}
                      >
                        <CardContent sx={{ p: '12px 16px', '&:last-child': { pb: '12px' } }}>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 34,
                                height: 34,
                                borderRadius: '8px',
                                bgcolor: 'background.paper',
                                border: '1px solid',
                                borderColor: card.borderColor,
                              }}
                            >
                              {card.icon}
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                {card.title}
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: 700, color: 'text.dark', fontSize: '15px' }}
                              >
                                {card.value}
                              </Typography>
                            </Box>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>

              <Divider />

              {/* Session Wise Analytics */}
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    mb: 2,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    color: 'text.secondary',
                    fontSize: '11px',
                  }}
                >
                  Session Usage
                </Typography>

                {!data.sessionWise || data.sessionWise.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No sessions recorded.
                  </Typography>
                ) : (
                  <TableContainer
                    component={Paper}
                    variant="outlined"
                    sx={{
                      borderRadius: '12px',
                      overflow: 'hidden',
                      borderColor: 'border.light',
                    }}
                  >
                    <Table size="small" aria-label="session usage table">
                      <TableHead sx={{ bgcolor: 'rgba(31, 30, 97, 0.02)' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, py: 1.5 }}>Org Code</TableCell>
                          <TableCell sx={{ fontWeight: 600, py: 1.5 }}>Session ID</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, py: 1.5 }}>
                            Rounds
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, py: 1.5 }}>
                            Tokens
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, py: 1.5 }}>
                            Cost
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.sessionWise.map((session, idx) => (
                          <TableRow
                            key={session.sessionId || idx}
                            sx={{
                              '&:hover': { bgcolor: 'action.hover' },
                              '&:last-child td, &:last-child th': { border: 0 },
                            }}
                          >
                            <TableCell sx={{ py: 1.25, fontWeight: 500 }}>{session.orgCode}</TableCell>
                            <TableCell sx={{ py: 1.25 }}>
                              <CopyableSessionId id={session.sessionId} />
                            </TableCell>
                            <TableCell align="right" sx={{ py: 1.25 }}>
                              {session.roundCount.toLocaleString()}
                            </TableCell>
                            <TableCell align="right" sx={{ py: 1.25 }}>
                              {session.totalTokens.toLocaleString()}
                            </TableCell>
                            <TableCell align="right" sx={{ py: 1.25, fontWeight: 600, color: 'primary.main' }}>
                              {formatCost(session.totalCost)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>

              <Divider />

              {/* Tool Wise Analytics */}
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    mb: 2,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    color: 'text.secondary',
                    fontSize: '11px',
                  }}
                >
                  Tool Usage
                </Typography>

                {!data.toolWise || data.toolWise.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No tools recorded.
                  </Typography>
                ) : (
                  <Stack spacing={1.5}>
                    {data.toolWise.map((tool, idx) => (
                      <Card
                        key={tool.tool || idx}
                        variant="outlined"
                        sx={{
                          borderRadius: '12px',
                          borderColor: 'border.light',
                          transition: 'all 0.2s',
                          '&:hover': {
                            borderColor: 'primary.main',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.04)',
                          },
                        }}
                      >
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
                            <Stack direction="row" alignItems="center" gap={1}>
                              <BuildIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontWeight: 600,
                                  color: 'text.purple',
                                  fontFamily: 'monospace',
                                  fontSize: '13px',
                                }}
                              >
                                {tool.tool}
                              </Typography>
                            </Stack>
                            <Box
                              sx={{
                                px: 1,
                                py: 0.25,
                                borderRadius: '6px',
                                bgcolor: alpha(theme.palette.secondary.main, 0.08),
                                color: 'secondary.main',
                                fontSize: '11px',
                                fontWeight: 700,
                              }}
                            >
                              {tool.callCount.toLocaleString()} Calls
                            </Box>
                          </Stack>
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                                Total Tokens
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.dark' }}>
                                {tool.totalTokens.toLocaleString()}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                                Total Cost
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                {formatCost(tool.totalCost)}
                              </Typography>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                )}
              </Box>

              <Divider />

              {/* User Usage Analytics */}
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    mb: 2,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    color: 'text.secondary',
                    fontSize: '11px',
                  }}
                >
                  User Usage Analytics
                </Typography>

                {userDetailsLoading ? (
                  <UserSkeletons />
                ) : !data.userWise || data.userWise.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No user usage recorded.
                  </Typography>
                ) : (
                  <Stack spacing={1.5}>
                    {data.userWise.map((user, idx) => {
                      const details = userDetails[user.userId];
                      const name = details?.name || 'Unknown User';
                      const email = details?.email || '--';

                      return (
                        <Card
                          key={user.userId || idx}
                          variant="outlined"
                          sx={{
                            borderRadius: '12px',
                            borderColor: 'border.light',
                            transition: 'all 0.2s',
                            '&:hover': {
                              borderColor: 'primary.main',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.04)',
                            },
                          }}
                        >
                          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: 38,
                                  height: 38,
                                  borderRadius: '50%',
                                  bgcolor: 'rgba(31, 30, 97, 0.05)',
                                  color: 'primary.main',
                                  fontWeight: 700,
                                  fontSize: '15px',
                                }}
                              >
                                {name.charAt(0).toUpperCase()}
                              </Box>
                              <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.dark' }}>
                                  {name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {email}
                                </Typography>
                              </Box>
                            </Stack>

                            <Stack direction="row" alignItems="center" gap={1} mb={2}>
                              <Typography variant="caption" color="text.secondary">
                                User ID:
                              </Typography>
                              <CopyableSessionId id={user.userId} />
                            </Stack>

                            <Grid container spacing={2} sx={{ mb: 2 }}>
                              <Grid item xs={4}>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: 'block', mb: 0.25 }}
                                >
                                  Tokens
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.dark' }}>
                                  {user.totalTokens.toLocaleString()}
                                </Typography>
                              </Grid>
                              <Grid item xs={4}>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: 'block', mb: 0.25 }}
                                >
                                  Rounds
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.dark' }}>
                                  {user.roundCount.toLocaleString()}
                                </Typography>
                              </Grid>
                              <Grid item xs={4}>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: 'block', mb: 0.25 }}
                                >
                                  Cost
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                  {formatCost(user.totalCost)}
                                </Typography>
                              </Grid>
                            </Grid>

                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block', mb: 0.75, fontWeight: 500 }}
                            >
                              Tool Usage
                            </Typography>
                            {user.toolUsage && user.toolUsage.length > 0 ? (
                              <Stack direction="row" flexWrap="wrap" gap={0.75}>
                                {user.toolUsage.map((tool, tIdx) => (
                                  <Tooltip title={`${tool.callCount.toLocaleString()} calls`} key={tIdx} arrow>
                                    <Box
                                      sx={{
                                        px: 1,
                                        py: 0.25,
                                        borderRadius: '6px',
                                        bgcolor: alpha(theme.palette.secondary.main, 0.08),
                                        color: 'secondary.main',
                                        fontSize: '11px',
                                        fontWeight: 700,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '11px' }}
                                      >
                                        {tool.tool}
                                      </Typography>
                                      <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '11px' }}>
                                        ({tool.callCount.toLocaleString()})
                                      </Typography>
                                    </Box>
                                  </Tooltip>
                                ))}
                              </Stack>
                            ) : (
                              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                No tools used.
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Stack>
                )}
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

interface BotAnalyticsProps {
  icon: React.ReactNode;
}

const TriggerWrapper = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '44px',
  width: '44px',
  backgroundColor: '#f4f3f7',
  borderRadius: '12px',
  marginRight: '10px',
  position: 'relative',
  cursor: 'pointer',
  transition: 'background-color 0.2s, transform 0.1s',
  color: '#0f0e47',
  '&:hover': {
    backgroundColor: '#e8e7ec',
    transform: 'scale(1.02)',
  },
  '&:active': {
    transform: 'scale(0.98)',
  },
}));

export const BotAnalytics: React.FC<BotAnalyticsProps> = ({ icon }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <TriggerWrapper onClick={() => setOpen(true)}>{icon}</TriggerWrapper>
      <BotAnalyticsDrawer open={open} onClose={() => setOpen(false)} />
    </>
  );
};

export default BotAnalytics;
